import {AUTH_TYPE} from '@Libraries/login-signup-library';
import {switchToConfirmLogin} from '@Components/login-page/login-page-reducer';
import type {ConfirmLoginState} from '@Components/login-page/login-page.types';
import {useLoginPageAuthType} from '@Components/login-page/login-page.hooks';
import {useAppDispatch} from '@/hooks';
import {useUpdateAuthFormTypes} from '@/hooks/login-signup/useUpdateAuthFormTypes';
import {useUpdateErrorStates} from '@/hooks/login-signup/useUpdateErrorStates';

export const useLoginError = () => {
  const authType = useLoginPageAuthType();
  const dispatch = useAppDispatch();

  const {showErrors, hideErrors} = useUpdateErrorStates();
  const {updateAuthTypes} = useUpdateAuthFormTypes();

  const onLoginError = (error: string, onAuthTypeChangeCallback: Function, email = '', password = '') => {
    let errorMessage = '';
    let section = authType;

    switch (error) {
      case 'user-blocked':
        errorMessage = window.i18next.t('pmwjs_user_account_blocked');
        break;
      case 'too-many-inactive-password-reset-attempts':
        errorMessage = window.i18next.t('pmwjs_too_many_inactive_user_password_resets');
        break;
      case 'low-recaptcha-v3-score':
        section = AUTH_TYPE.CONFIRM_LOGIN;
        hideErrors();
        const confirmLoginData: ConfirmLoginState = {
          confirmLoginType: AUTH_TYPE.LOGIN,
          pendingLoginEmail: email,
          pendingLoginPassword: password,
        };
        dispatch(switchToConfirmLogin(confirmLoginData));
        break;

      case 'low-recaptcha-v2-score':
        errorMessage = window.i18next.t('pmwjs_robot_detected_error');
        updateAuthTypes(AUTH_TYPE.LOGIN);
        section = AUTH_TYPE.LOGIN;
        break;

      // An email that only has an SSO login reports the same failure as a wrong password: naming the
      // provider tells an attacker the account exists and which button to attack. Owner + designer call.
      default:
        errorMessage = getInvalidCredentialsMessage();
    }

    if (authType === AUTH_TYPE.CONFIRM_LOGIN) {
      section = AUTH_TYPE.LOGIN;
      updateAuthTypes(AUTH_TYPE.LOGIN);
    }

    if (authType === section) {
      showErrors(errorMessage);
    } else {
      onAuthTypeChangeCallback(errorMessage);
    }
  };

  /** On a remembered account's password step the email is fixed, so the failure can name the password. */
  const getInvalidCredentialsMessage = (): string => {
    return authType === AUTH_TYPE.SAVED_ACCOUNT_PASSWORD ? window.i18next.t('pmwjs_incorrect_password_try_again') : window.i18next.t('pmwjs_invalid_credentials');
  };

  return {onLoginError};
};
